/**
 * @typedef  { Object }               FlowData
 * @property { string | null }        heading
 * @property { string | null }        subtext
 * @property { Bundle[ ] }            bundles
 * @property { "pdp" | "collection" } type
 */

/**
 * @typedef  { Object }         Product
 * @property { string }         variant_name
 * @property { string }         product_name
 * @property { string }         image
 * @property { number }         id
 * @property { number }         inventory_quantity
 * @property { ProductOptions } options
 */

/**
 * @typedef { Object }     ProductOptions
 * @property { String }    name
 * @property { String[ ] } value
 */

/**
 * @typedef  { Object }     Step
 * @property { string }     heading
 * @property { string }     subtext
 * @property { string }     id
 * @property { Product[ ] } products
 * @property { number }     min_quantity
 * @property { number }     max_quantity
 * @property { boolean }    category_in_summary
 * @property { string }     category_name
 * @property { string[ ] }  filters
 * @property { string[ ] }  filter_names
 */

/**
 * @typedef  { Object }  Bundle
 * @property { string }  name
 * @property { string }  subtext
 * @property { string }  image
 * @property { number }  id
 * @property { number }  variant_id
 * @property { string }  price
 * @property { Step[ ] } steps
 * @property { number }  min_quantity
 * @property { number }  max_quantity
 */

/**
 * @typedef { Object }  Filter
 * @property { string } name
 * @property { string } value
 */

/**
 * @typedef  { Object }          FlowStateProduct
 * @property { number | string } variant_id
 * @property { number | string } quantity
 */

/**
 * @typedef  { Object }              FlowStateStepData
 * @property { string }              step
 * @property { FlowStateProduct[ ] } products
 */

/**
 * @typedef  { Object }                 FlowState
 * @property { number | string | null } bundle
 * @property { string | null }          selected_step
 * @property { FlowStateStepData[ ] }   step_data
 */

/**
 * @typedef { Window &
 * {
 *   _flowData:  FlowData | null,
 *   _flowState: FlowState
 * } } FlowWindow
 */

/** @type  { FlowWindow } */
const flowWindow = window;

/** @type { FlowData | null } */
flowWindow._flowData = null;

/** @type { FlowState } */
flowWindow._flowState =
{
    bundle: null,
    selected_step: null,
    step_data: []
};

const LOCAL_STORAGE_KEY_BASE = "bundleCreatorFlowState_"

const IMAGE_SIZES = [ 352, 600 ];

/**
 * 
 * @param { HTMLImageElement }          element 
 * @param { string }                    imageSrc
 * @param { "top" | "center" | "bottom" | "left" | "right" | "region" } crop
 * @param { number[ ] }                 imageSizes
 * @param { number }                    baseSize
 * @param { "auto" | "low" | "high" }   fetchPriority
 * @param { "auto" | "lazy" | "eager" } loading
 * @returns { HTMLImageElement }
 */
function CreateResponsiveImage( element, imageSrc, crop, imageSizes, baseSize, fetchPriority, loading )
{
    const cloneImage = element.cloneNode( true );

    var baseUrl = imageSrc;
    if ( baseUrl.indexOf( "?" ) )
    {
        baseUrl = baseUrl.slice( 0, baseUrl.indexOf( "?" ) );
    }

    const baseSrc = baseUrl + `?crop=${crop}&height=${baseSize}&width=${baseSize}`;
    var srcSet    = "";

    imageSizes.forEach( ( imageSize, index ) =>
    {
        const sizeSrc = baseUrl + `?crop=${crop}&height=${imageSize}&width=${imageSize} ${imageSize}w`;
        srcSet += sizeSrc;

        if ( index != imageSizes.length - 1 )
        {
            srcSet += ", ";
        }
    } );

    cloneImage.setAttribute( "src", baseSrc );
    cloneImage.setAttribute( "srcset", srcSet );
    cloneImage.setAttribute( "width", baseSize );
    cloneImage.setAttribute( "height", baseSize );
    cloneImage.setAttribute( "fetchpriority", fetchPriority );
    cloneImage.setAttribute( "loading", loading );

    return cloneImage;
};

/**
 * 
 * @param { string } bundleId 
 * @returns { number }
 */
function GetIndexOfBundle( bundleId )
{
    return flowWindow._flowData.bundles.findIndex( bundle => { return bundle.id == bundleId } );
}

/**
 * 
 * @param { string } stepId 
 * @param { string } bundleId 
 * @returns { number }
 */
function GetIndexOfStep( stepId, bundleId )
{
    var stepIndex = -1;
    const bundleIndex = GetIndexOfBundle( bundleId );

    if ( bundleIndex !== -1 )
    {
        stepIndex = flowWindow._flowData.bundles[ bundleIndex ].steps.findIndex( step => { return step.id == stepId } );
    }

    return stepIndex;
}

/**
 * @returns { Step }
 */
function GetCurrentStep( )
{
    var step = null;

    if ( flowWindow._flowState.bundle && flowWindow._flowState.selected_step )
    {
        const bundleIndex = GetIndexOfBundle( flowWindow._flowState.bundle );
        const stepIndex   = GetIndexOfStep( flowWindow._flowState.selected_step, flowWindow._flowState.bundle );
        if ( stepIndex !== -1 )
        {
            step = flowWindow._flowData.bundles[ bundleIndex ].steps[ stepIndex ];
        }
    }

    return step;
}

/**
 * @param { string } bundleId 
 * @param { string } stepId 
 */
function GetMaximumUseableQuantity( bundleId, stepId )
{
    let currentQuantity = 0;
    const bundle        = flowWindow._flowData.bundles[ GetIndexOfBundle( bundleId ) ]
    const stepIndex     = GetIndexOfStep( stepId, bundleId );
    const step          = bundle.steps[ stepIndex ];
    let newMax          = step.max_quantity;

    flowWindow._flowState.step_data.forEach( stepData =>
    {
        var stepQuantity = 0;
        stepData.products.forEach( product =>
        {
            stepQuantity = stepQuantity + parseInt( product.quantity );
        } );

        currentQuantity = currentQuantity + stepQuantity;
    } );

    if ( stepIndex < bundle.steps.length - 1 )
    {
        const remainingSteps = bundle.steps.slice( stepIndex + 1 );
        var minimumRequired  = 0;

        remainingSteps.forEach( remainingStep =>
        {
            minimumRequired = minimumRequired + parseInt( remainingStep.min_quantity )
        } );

        newMax = bundle.max_quantity - currentQuantity - minimumRequired;
    }

    return newMax;
}

/**
 * @param { HTMLElement } element 
 * @param { Object } object 
 */
function ElementFillFromObject( element, object )
{
    const objectKeys = Object.keys( object );
    const elsToClear = element.querySelectorAll( "[data-key]" );
    elsToClear.forEach( elToClear =>
    {
        elToClear.textContent = "";
    } );

    objectKeys.forEach( key =>
    {
        const matchingEls = element.querySelectorAll( `[data-key="${key}"]` );
        matchingEls.forEach( matchingEl =>
        {
            if ( matchingEl.dataset.type == "text" )
            {
                matchingEl.textContent = object[ key ];
            }
            else if ( matchingEl.dataset.type == "image" )
            {
                matchingEl.setAttribute( "src",  object[ key ] );
            }
        } );
    } );
}

/**
 * @param { string }     bundleId 
 * @param { string }     stepId 
 * @param { Product[ ] } products 
 * @param { boolean }    merge 
 */
function WriteFlowState( bundleId, stepId, products, merge )
{
    const bundleIndex = GetIndexOfBundle( bundleId );

    if ( bundleIndex !== -1 )
    {
        if ( stepId )
        {
            const stepIndex = GetIndexOfStep( stepId, bundleId );

            flowWindow._flowState.bundle = bundleId;

            if ( flowWindow._flowData.bundles[ bundleIndex ].steps.length > stepIndex + 1 )
            {
                flowWindow._flowState.selected_step = flowWindow._flowData.bundles[ bundleIndex ].steps[ stepIndex + 1 ].id;
            }
            else
            {
                flowWindow._flowState.selected_step = flowWindow._flowData.bundles[ bundleIndex ].steps.slice( -1 )[ 0 ].id;
            }

            if ( products )
            {
                OverwriteOrAddStep( bundleIndex, stepIndex, products, merge );
            }
        }
        else
        {
            flowWindow._flowState.bundle        = bundleId;
            flowWindow._flowState.selected_step = flowWindow._flowData.bundles[ bundleIndex ].steps[ 0 ].id;
            flowWindow._flowState.step_data     = [ ];
        }
    }

    localStorage.setItem( LOCAL_STORAGE_KEY_BASE + window.location.pathname.slice( 1 ), JSON.stringify( flowWindow._flowState ) );
}

/**
 * @param { number }     bundleIndex 
 * @param { number }     stepIndex 
 * @param { Product[ ] } products 
 * @param { boolean }    merge
 */
function OverwriteOrAddStep( bundleIndex, stepIndex, products, merge )
{
    if ( merge === true && flowWindow._flowState.step_data[ stepIndex ] )
    {
        products.forEach( product =>
        {
            var matchingProduct = flowWindow._flowState.step_data[ stepIndex ].products.find( productSaved =>
            {
                return productSaved.variant_id == product.variant_id;
            } );

            if ( matchingProduct )
            {
                matchingProduct.quantity = product.quantity;
            }
            else
            {
                flowWindow._flowState.step_data[ stepIndex ].products.push( product );
            }
        } )
    }
    else
    {
        const stepData =
        {
            "step":     flowWindow._flowData.bundles[ bundleIndex ].steps[ stepIndex ].id,
            "products": products
        };

        if ( flowWindow._flowState.step_data.length >= stepIndex + 1 )
        {
            flowWindow._flowState.step_data.splice( stepIndex );
        }

        flowWindow._flowState.step_data.push( stepData );
    }
}

/**
 * 
 * @param { number } bundleIndex 
 * @param { number } stepIndex 
 */
function ResetFlowStateToStep( bundleIndex, stepIndex )
{
    if ( flowWindow._flowData.bundles[ bundleIndex ].steps.length > stepIndex + 1 && stepIndex >= 0 )
    {
        flowWindow._flowState.step_data.splice( stepIndex );
        flowWindow._flowState.selected_step = flowWindow._flowData.bundles[ bundleIndex ].steps[ stepIndex ].id;
    }
}

/**
 * @returns { number }
 */
function getStepAddedQuantity( )
{
    const quantitySelectors = document.querySelectorAll( ".bundle-creator .bundle-creator__product-grid__product:not( .js-bc-product-template ) .js-bc-product-qs" );
    let quantity = 0;

    quantitySelectors.forEach( qs =>
    {
        quantity = quantity + parseInt( qs.input.value );
    } );

    return quantity;
}

/**
 * @returns { Product[ ] }
 */
function ParseSelectedProducts( )
{
    const productCards = document.querySelectorAll( ".bundle-creator .bundle-creator__product-grid__product:not( .js-bc-product-template )" );
    let products = [ ];

    productCards.forEach( productCard =>
    {
        const qs          = productCard.querySelector( ".js-bc-product-qs" );
        const variantName = productCard._variantName
        const productName = productCard._productName;
        const variantId   = productCard._variantId;
        const image       = productCard._image;
        const quantity    = parseInt( qs.input.value );

        if ( quantity > 0 )
        {
            products.push( 
            {
                variant_name: variantName,
                product_name: productName,
                variant_id:   variantId,
                image:        image,
                quantity:     quantity
            } );
        }
    } );

    return products;
}

/**
 * @returns { boolean }
 */
function ValidateFlowComplete( )
{
    const currentBundleId    = flowWindow._flowState.bundle;
    const currentBundleIndex = GetIndexOfBundle( currentBundleId );
    var quantityValid        = true;
    var eachStepCompleted    = true;
    var flowComplete         = false;

    if ( currentBundleIndex > -1 )
    {
        const currentBundle  = flowWindow._flowData.bundles[ currentBundleIndex ];

        currentBundle.steps.forEach( step =>
        {
            var stepDataFound = false;

            flowWindow._flowState.step_data.forEach( stepData =>
            {
                if ( stepData.step === step.id )
                {
                    stepDataFound = true;
                }
            } );

            eachStepCompleted = stepDataFound;
        } );

        if ( eachStepCompleted )
        {
            var totalQuantity = 0;

            flowWindow._flowState.step_data.forEach( step =>
            {
                var stepQuantity = 0;
                const stepIndex  = GetIndexOfStep( step.step, currentBundleId );
                const stepObj    = currentBundle.steps[ stepIndex ];

                step.products.forEach( product =>
                {
                    stepQuantity += product.quantity;
                } );

                if ( stepQuantity < stepObj.min_quantity || stepQuantity > stepObj.max_quantity )
                {
                    quantityValid = false;
                }

                totalQuantity += stepQuantity;
            } );

            if ( totalQuantity < currentBundle.min_quantity || totalQuantity > currentBundle.max_quantity )
            {
                quantityValid = false;
            }
        }

        flowComplete = quantityValid && eachStepCompleted;
    }

    return flowComplete;
}

/**
 * 
 * @param { string } bundleId 
 * @param { string } stepId 
 * @param { number } variantId 
 */
function RemoveProductFromState( bundleId, stepId, variantId )
{
    const bundleIndex = GetIndexOfBundle( bundleId );

    if ( bundleIndex !== -1 )
    {
        if ( stepId )
        {
            const stepIndex = GetIndexOfStep( stepId, bundleId );

            if ( flowWindow._flowState.step_data[ stepIndex ] )
            {
                var product = flowWindow._flowState.step_data[ stepIndex ].products.find( product =>
                {
                    return product.variant_id == variantId;
                } );

                if ( product )
                {
                    var index = flowWindow._flowState.step_data[ stepIndex ].products.indexOf( product );
                    if ( index !== -1 )
                    {
                        flowWindow._flowState.step_data[ stepIndex ].products.splice( index, 1 );
                    }
                }
            }
        }
    }

    localStorage.setItem( LOCAL_STORAGE_KEY_BASE + window.location.pathname.slice( 1 ), encodeURI( JSON.stringify( flowWindow._flowState ) ) );
}

/**
 * @param { Step } step
 */
function applyFilters( step )
{
    const filters         = document.querySelectorAll( ".filters__filter:not( .js-bc-controls-filter-template )" );
    
    const appliedFilters  = [ ];

    filters.forEach( filter =>
    {
        appliedFilters.push( { name: filter.dataset.internalName, value: filter.value } );
    } );

    renderProductGrid( step.products, step.max_quantity, appliedFilters );
}

function clearFilters( )
{
    const step    = GetCurrentStep( );
    const filters = document.querySelectorAll( ".filters__filter:not( .js-bc-controls-filter-template )" );

    if ( step )
    {
        renderProductGrid( step.products, step.max_quantity );
    }

    filters.forEach( filter =>
    {
        filter.clear( );
    } );
}

/**
 * @param { Step } step
 */
function renderFilters( step )
{
    const filterContainer = document.querySelector( ".js-bc-controls-filters" );
    const filterTemplate  = document.querySelector( ".js-bc-controls-filter-template" );
    const filterTypes = [];

    while ( filterContainer.querySelector( ".filters__filter:not( .js-bc-controls-filter-template )" ) )
    {
        filterContainer.querySelector( ".filters__filter:not( .js-bc-controls-filter-template )" ).remove( );
    }

    function getShownFIlterName( filterName )
    {
        const index = step.filters.indexOf( filterName );

        if ( index !== -1 && step.filter_names && step.filter_names.length && step.filter_names.length > index )
        {
            return step.filter_names[ index ];
        }

        return null;
    }

    step.products.forEach( product =>
    {
        if ( product.options && product.options.length )
        {
            product.options.forEach( option =>
            {
                var matchingType = filterTypes.find( filterType =>
                {
                    return filterType.name == option.name;
                } );

                if ( matchingType )
                {
                    if ( option.value instanceof Array )
                    {
                        option.value.forEach( value =>
                        {
                            if ( !matchingType.values.includes( value.trim( ) ) )
                            {
                                matchingType.values.push( value.trim( ) );
                            }
                        } );
                    }
                    else if ( !matchingType.values.includes( option.value.trim( ) ) )
                    {
                        matchingType.values.push( option.value.trim( ) );
                    }
                }
                else
                {
                    if ( option.value instanceof Array )
                    {
                        option.value.forEach( value =>
                        {
                            filterTypes.push( { shownName: getShownFIlterName( option.name ), name: option.name, values: [ value.trim( ) ] } );
                        } );
                    }
                    else
                    {
                        filterTypes.push( { shownName: getShownFIlterName( option.name ), name: option.name, values: [ option.value.trim( ) ] } );
                    }
                }
            } );
        }
    } );

    step.filters.forEach( ( filter, index ) =>
    {
        var matchingType = filterTypes.find( filterType =>
        {
            return filterType.name == filter;
        } );

        if ( matchingType && matchingType.values.length > 0 )
        {
            const clone = filterTemplate.cloneNode( true );

            clone.classList.remove( "js-bc-controls-filter-template" );
            clone.dataset.internalName = filter;
            clone.querySelector( ".js-custom-select-open-button" ).setAttribute( "aria-controls", `${ filter }-${ index }` );
            clone.querySelector( ".custom-select__modal" ).setAttribute( "id",`${ filter }-${ index }` );
            clone.removeAttribute( "aria-hidden" );

            if ( matchingType.shownName !== null )
            {
                clone.dataset.shownName = matchingType.shownName;
            }
            else
            {
                clone.dataset.shownName = filter;
            }

            const optionContainer      = clone.querySelector( ".js-custom-select-modal" );
            const optionTemplate       = optionContainer.querySelector( ".js-custom-select-option-template" );
            
            matchingType.values.forEach( filterValue =>    
            {
                const optionClone = optionTemplate.cloneNode( true );

                optionClone.classList.remove( "js-custom-select-option-template" );
                optionClone.dataset.name  = filterValue;
                optionClone.dataset.value = filterValue;
                optionClone.textContent   = filterValue;

                optionContainer.appendChild( optionClone );
            })

            clone.addEventListener( "change", ( ) => { applyFilters( step ); } );

            filterContainer.appendChild( clone );
        }
    } );
}

/**
 * 
 * @param { Object }  object 
 * @param { boolean } showSummary 
 * @param { boolean } enableBackNavigation 
 */
function renderHeader( object, showSummary, enableBackNavigation )
{
    const header           = document.querySelector( ".js-bc-header" );
    const headerControls   = header.querySelector( ".js-bc-header-controls" );
    const quantityDisplays = header.querySelectorAll( ".js-bc-controls-quantity" );
    const backBtn          = header.querySelector( ".js-bc-controls-back" );
    const filters          = header.querySelector( ".js-bc-controls-filters" );
    const continueBtn      = header.querySelector( ".js-bc-controls-continue" );

    ElementFillFromObject( header, object );

    continueBtn.setAttribute( "disabled", "" );

    quantityDisplays.forEach( quantityDisplay =>
    {
        if ( object.max_quantity && object.max_quantity > 1 )
        {
            quantityDisplay.textContent   = `Add ${ object.max_quantity } more ${ object.max_quantity == 1 ? "item" : "items" } to continue.`;
            quantityDisplay.style.display = "";
        }
        else
        {
            quantityDisplay.style.display = "none";
        }
    } )

    if ( showSummary )
    {
        headerControls.classList.add( "text-controls__controls--active" );
    }
    else
    {
        headerControls.classList.remove( "text-controls__controls--active" );
    }

    if ( object.filters && object.filters.length && object.filters.length > 0 )
    {
        renderFilters( object );
        filters.classList.add( "bundle-creator__header__filters--active" );
        filters.style.height = filters.scrollHeight + "px";
    }
    else
    {
        filters.classList.remove( "bundle-creator__header__filters--active" );
        filters.style.height = "";
    }

    if ( enableBackNavigation === true )
    {
        backBtn.removeAttribute( "disabled" );
    }
    else
    {
        backBtn.setAttribute( "disabled", "" );
    }
}

function handleBundleSelect( event )
{
    const card  = event.target.closest( ".bundle-creator__product-grid__bundle" );
    WriteFlowState( card._bundleId );
    renderFlow( );
}

function handleProductSelect( event )
{
    const card             = event.target.closest( ".bundle-creator__product-grid__product" );
    const button           = event.target.closest( ".js-bc-product-select-button" );
    const selected         = card.classList.contains( "bundle-creator__product-grid__product--selected" );
    const variantName      = card._variantName;
    const productName      = card._productName;
    const variantId        = card._variantId;
    const image            = card._image;
    const bundleIndex      = GetIndexOfBundle( flowWindow._flowState.bundle );
    const currentStepIndex = GetIndexOfStep( flowWindow._flowState.selected_step, flowWindow._flowState.bundle );
    const currentStep      = flowWindow._flowData.bundles[ bundleIndex ].steps[ currentStepIndex ];
    const continueBtns     = document.querySelectorAll( ".js-bc-controls-continue" );
    const quantityDisplays = document.querySelectorAll( ".js-bc-controls-quantity" );

    if ( selected )
    {
        WriteFlowState( flowWindow._flowState.bundle, flowWindow._flowData.bundles[ bundleIndex ].steps[ currentStepIndex ].id, [ ] );
        card.classList.remove( "bundle-creator__product-grid__product--selected" );
        button.textContent = button.dataset.selectText;
    }
    else if ( flowWindow._flowState.step_data.length >= currentStepIndex + 1 && flowWindow._flowState.step_data[ currentStepIndex ].products.length > 0 )
    {
        flowWindow._flowState.step_data[ currentStepIndex ].products.forEach( productData =>
        {
            const matchingProduct = document.querySelector( `.bundle-creator__product-grid__product[data-id="${ productData.variant_id }"]` );

            if ( matchingProduct )
            {
                matchingProduct.classList.remove( "bundle-creator__product-grid__product--selected" );
                matchingProduct.querySelector( ".js-bc-product-select-button" ).textContent = matchingProduct.querySelector( ".js-bc-product-select-button" ).dataset.selectText;
            }
        } );
        WriteFlowState( flowWindow._flowState.bundle, flowWindow._flowData.bundles[ bundleIndex ].steps[ currentStepIndex ].id, [ ] );
    }

    const stepData =
    {
        variant_name: variantName,
        product_name: productName,
        variant_id:   variantId,
        image:        image,
        quantity:     1
    };

    if ( currentStepIndex + 1 < flowWindow._flowData.bundles[ bundleIndex ].steps.length )
    {
        WriteFlowState( flowWindow._flowState.bundle, flowWindow._flowData.bundles[ bundleIndex ].steps[ currentStepIndex ].id, [ stepData ] );
        renderFlow( );
    }
    else if ( !selected )
    {
        WriteFlowState( flowWindow._flowState.bundle, flowWindow._flowData.bundles[ bundleIndex ].steps[ currentStepIndex ].id, [ stepData ] );
        card.classList.add( "bundle-creator__product-grid__product--selected" );
        button.textContent = "Remove";
    }

    if ( currentStepIndex + 1 === flowWindow._flowData.bundles[ bundleIndex ].steps.length )
    {
        const maxQuantity  = GetMaximumUseableQuantity( flowWindow._flowState.bundle, flowWindow._flowState.selected_step );
        if ( flowWindow._flowState.step_data.length == currentStepIndex + 1 && flowWindow._flowState.step_data[ currentStepIndex ].products.length > 0 )
        {
            const stepQuantity = flowWindow._flowState.step_data[ currentStepIndex ].products[ 0 ].quantity;
            if ( currentStep && stepQuantity >= currentStep.min_quantity && stepQuantity <= maxQuantity )
            {
                continueBtns.forEach( continueBtn =>
                {
                    continueBtn.removeAttribute( "disabled" );
                } );
            }
        }
        else
        {
            continueBtns.forEach( continueBtn =>
            {
                continueBtn.setAttribute( "disabled", "" );
            } );
        }
    }
}

function handleQSChange( event )
{
    const card             = event.target.closest( ".bundle-creator__product-grid__product" );
    const cardQuantity     = card.querySelector( ".js-bc-product-qs input" ).value;
    const quantity         = getStepAddedQuantity( );
    const quantityDisplays = document.querySelectorAll( ".js-bc-controls-quantity" );
    const continueBtns     = document.querySelectorAll( ".js-bc-controls-continue" );
    const currentStep      = GetCurrentStep( );
    const maxQuantity      = GetMaximumUseableQuantity( flowWindow._flowState.bundle, flowWindow._flowState.selected_step );

    if ( currentStep && quantity >= currentStep.min_quantity && quantity <= maxQuantity )
    {
        continueBtns.forEach( continueBtn =>
        {
            continueBtn.removeAttribute( "disabled" );
        } );

        quantityDisplays.forEach( quantityDisplay =>
        {
            quantityDisplay.textContent = "Enough items added, you can continue.";
        } );
    }
    else
    {
        continueBtns.forEach( continueBtn =>
        {
           continueBtn.setAttribute( "disabled", "" );
        } );

        quantityDisplays.forEach( quantityDisplay =>
        {
            if ( quantity < currentStep.min_quantity )
            {
                quantityDisplay.textContent = `Add ${ currentStep.min_quantity - quantity } more ${ currentStep.min_quantity - quantity == 1 ? "item" : "items" } to continue.`;
            }
            else if ( quantity > maxQuantity )
            {
                quantityDisplay.textContent = `Remove ${ quantity - currentStep.max_quantity } more ${ quantity - currentStep.max_quantity == 1 ? "item" : "items" } to continue.`;
            }
            else
            {
                quantityDisplay.textContent = "Unable to get flow info.";
            }
        } );
    }

    if ( parseInt( cardQuantity ) > 0 )
    {
        card.classList.add( "bundle-creator__product-grid__product--selected" );
        WriteFlowState( flowWindow._flowState.bundle, flowWindow._flowState.selected_step, ParseSelectedProducts( ), true );
    }
    else
    {
        card.classList.remove( "bundle-creator__product-grid__product--selected" );
        RemoveProductFromState( flowWindow._flowState.bundle, flowWindow._flowState.selected_step, card._variantId )
    }
}

/**
 * @param { Bundle }  bundle
 * @param { boolean } aboveTheFold
 */
function renderBundle( bundle, aboveTheFold )
{
    const template    = document.querySelector( ".js-bc-bundle-template" );
    const clone       = template.cloneNode( true );
    const image       = clone.querySelector( ".js-bc-bundle-image" );
    var loading       = "lazy";
    var fetchPriority = "low";

    if ( aboveTheFold === true )
    {
        loading = "eager";
        fetchPriority = "high";
    }

    ElementFillFromObject( clone, bundle );
    clone.classList.remove( "js-bc-bundle-template" );
    clone._name     = bundle.name;
    clone._bundleId = bundle.id;
    clone._image    = bundle.image;

    clone.querySelector( ".js-bc-bundle-select-button" ).addEventListener( "click", handleBundleSelect );

    const responsiveImage = CreateResponsiveImage( image, bundle.image, "center", IMAGE_SIZES, 600, fetchPriority, loading );
    responsiveImage.alt = bundle.name;
    image.replaceWith( responsiveImage );

    return clone;
}

/**
 * 
 * @param { Product } product 
 * @param { number }  quantityLimit
 * @param { boolean } aboveTheFold
 */
function renderProduct( product, quantityLimit, aboveTheFold )
{
    const template    = document.querySelector( ".js-bc-product-template" );
    const clone       = template.cloneNode( true );
    const select      = clone.querySelector( ".js-bc-product-select-button" );
    const qs          = clone.querySelector( ".js-bc-product-qs" );
    const tag         = clone.querySelector( ".js-bc-product-tag" );
    const stepIndex   = GetIndexOfStep( flowWindow._flowState.selected_step, flowWindow._flowState.bundle );
    const image       = clone.querySelector( ".js-bc-product-image" );
    var loading       = "lazy";
    var fetchPriority = "low";

    if ( aboveTheFold === true )
    {
        loading = "eager";
        fetchPriority = "high";
    }

    function removeOOSTag( )
    {
        tag.classList.remove( "product__tag--oos" );
        tag.textContent = "";
    }

    function showOOSTag( )
    {
        tag.classList.add( "product__tag--oos" );
        tag.textContent = "Out of Stock";
    }
    
    if ( flowWindow._flowState.step_data[ stepIndex ] )
    {
        const currentSelectedProducts = flowWindow._flowState.step_data[ stepIndex ].products;
        const matchingProduct         = currentSelectedProducts.find( currentSelectedProduct =>
        {
            return currentSelectedProduct.variant_id == product.id;
        } );

        if ( matchingProduct && matchingProduct.quantity > 0 )
        {
            qs.querySelector( "input" ).value = matchingProduct.quantity;
            clone.classList.add( "bundle-creator__product-grid__product--selected" );
        }
    }

    ElementFillFromObject( clone, product );
    clone.classList.remove( "js-bc-product-template" );
    clone._variantName        = product.variant_name;
    clone._productName        = product.product_name;
    clone._variantId          = product.id;
    clone._image              = product.image;
    clone._options            = product.options;
    clone._inventory_quantity = product.inventory_quantity;
    clone.dataset.id          = product.id;

    if ( quantityLimit > 1 )
    {
        select.remove( );
        if ( product.inventory_quantity === 0 )
        {
            qs.querySelector( "input" ).max = 0;
            qs.querySelector( "input" ).setAttribute( "disabled", "" );
            showOOSTag( );
        }
        else
        {
            qs.addEventListener( "change", handleQSChange );
            qs.querySelector( "input" ).max = product.inventory_quantity;
            qs.querySelector( "input" ).removeAttribute( "disabled" );
            removeOOSTag( );
        }

        qs.querySelector( "button[name='plus'] .visually-hidden" ).textContent  += product.product_name + " - " + product.variant_name;
        qs.querySelector( "button[name='minus'] .visually-hidden" ).textContent += product.product_name + " - " + product.variant_name;
    }
    else
    {
        qs.remove( );
        if ( product.inventory_quantity === 0 )
        {
            select.setAttribute( "disabled", "" );
            showOOSTag( );
        }
        else
        {
            select.removeAttribute( "disabled" );
            select.addEventListener( "click", handleProductSelect );
            removeOOSTag( );
        }
    }

    const responsiveImage = CreateResponsiveImage( image, product.image, "center", IMAGE_SIZES, 600, fetchPriority, loading );
    responsiveImage.alt = product.product_name + " - " + product.variant_name;
    image.replaceWith( responsiveImage );

    return clone;
}

/**
 * @param { Bundle[ ] } bundles
 */
function renderBundleGrid( bundles )
{
    const productContainer = document.querySelector( ".js-bc-product-grid" );
    while ( productContainer.querySelector( ":is( .js-bc-product, .js-bc-bundle ):not( .js-bc-product-template, .js-bc-bundle-template )" ) )
    {
        productContainer.querySelector( ":is( .js-bc-product, .js-bc-bundle ):not( .js-bc-product-template, .js-bc-bundle-template )" ).remove( );
    }

    bundles.forEach( ( bundle, index ) =>
    {
        productContainer.appendChild( renderBundle( bundle, index <= 7 ) );
    } );
}

/**
 * @param { Filter[ ] }  filters
 * @param { Product[ ] } products
 */
function getProductsMatchingFilters( products, filters )
{
    return products.filter( ( product ) =>
        filters.every( ( filter ) =>
        {
            if ( filter.value !== null )
            {
                return product.options.some( ( option ) =>
                {
                    if ( option.value instanceof Array )
                    {
                        return filter.name === option.name && option.value.includes( filter.value );
                    }
                    else
                    {
                        return filter.name === option.name && option.value === filter.value;
                    }
                } );
            }
            else
            {
                return true;
            }
        } )
    );
}

/**
 * @param { Product[ ] } products
 * @param { number }     quantityLimit 
 * @param { Filter[ ] }  filters
 */
function renderProductGrid( products, quantityLimit, filters )
{
    const productContainer = document.querySelector( ".js-bc-product-grid" );
    const clearFiltersBtn  = document.querySelector( ".js-bc-controls-filters-clear" );
    while ( productContainer.querySelector( ":is( .js-bc-product, .js-bc-bundle ):not( .js-bc-product-template, .js-bc-bundle-template )" ) )
    {
        productContainer.querySelector( ":is( .js-bc-product, .js-bc-bundle ):not( .js-bc-product-template, .js-bc-bundle-template )" ).remove( );
    }

    if ( filters && filters.length && filters.length > 0 )
    {
        const filteredProducts = getProductsMatchingFilters( products, filters );
        filteredProducts.forEach( ( product, index ) =>
        {
            productContainer.appendChild( renderProduct( product, quantityLimit, index <= 7 ) );
        } );
        clearFiltersBtn.removeAttribute( "disabled" );
    }
    else
    {
        products.forEach( ( product, index ) =>
        {
            productContainer.appendChild( renderProduct( product, quantityLimit, index <= 7 ) );
        } );
        clearFiltersBtn.setAttribute( "disabled", "" );
    }
}

function renderFlow( )
{
    const bundle       = flowWindow._flowState.bundle;
    const selectedStep = flowWindow._flowState.selected_step;

    if ( bundle && selectedStep )
    {
        const bundleIndex = GetIndexOfBundle( bundle );
        const stepIndex   = GetIndexOfStep( selectedStep, bundle );
        const step        = flowWindow._flowData.bundles[ bundleIndex ].steps[ stepIndex ];

        if ( stepIndex === 0 && flowWindow._flowData.type === "pdp" )
{
    const showSummary = flowWindow._flowData.bundles[ bundleIndex ].steps.length === 1;
    renderHeader( step, showSummary, false );
}
        else
        {
            var showSummary = false
            if ( stepIndex == flowWindow._flowData.bundles[ bundleIndex ]?.steps.length - 1 )
            {
                showSummary = true
            }

            var enableBackNavigation = false;
            if ( selectedStep || ( flowWindow._flowData.type === "pdp" > 1 && selectedStep ) )
            {
                enableBackNavigation = true;
            }

            renderHeader( step, showSummary, enableBackNavigation );
        }

        renderProductGrid( step.products, step.max_quantity );
    }
    else
    {
        renderHeader( { "heading": flowWindow._flowData.heading, "subtext": flowWindow._flowData.subtext }, false );
        renderBundleGrid( flowWindow._flowData.bundles );
    }
}

function renderSummary( )
{
    const summary      = document.querySelector( ".js-bc-summary" );
    const container    = summary.querySelector( ".js-bc-summary-items" );
    const typeTemplate = container.querySelector( ".js-bc-summary-item-type-template" );
    const itemTemplate = container.querySelector( ".js-bc-summary-item-template" );
    const totalEl      = summary.querySelector( ".js-bc-summary-total" );
    const bundle       = flowWindow._flowData.bundles[ GetIndexOfBundle( flowWindow._flowState.bundle ) ];

    while ( container.querySelector( ".items__item-type:not( .js-bc-summary-item-type-template )" ) )
    {
        container.querySelector( ".items__item-type:not( .js-bc-summary-item-type-template )" ).remove( );
    }

    bundle.steps.forEach( ( step, index ) =>
    {
        if ( step.category_in_summary && flowWindow._flowState.step_data[ index ] && flowWindow._flowState.step_data[ index ].products.length > 0 )
        {
            const typeEl = typeTemplate.cloneNode( true );
            typeEl.classList.remove( "js-bc-summary-item-type-template" );
            ElementFillFromObject( typeEl, step );
            
            flowWindow._flowState.step_data[ index ].products.forEach( product =>
            {
                const itemEl = itemTemplate.cloneNode( true );
                itemEl.classList.remove( "js-bc-summary-item-template" );
                ElementFillFromObject( itemEl, product );

                typeEl.querySelector( ".js-bc-summary-item-grid" ).appendChild( itemEl );
            } );

            container.appendChild( typeEl );
        }
    } );

    totalEl.textContent = bundle.price;
}

function handleAddToCart( )
{
    if ( ValidateFlowComplete( ) )
    {
        const bundle = flowWindow._flowData.bundles[ GetIndexOfBundle( flowWindow._flowState.bundle ) ];

        const formData =
        {
            "items":
            [
                {
                    "id": bundle.variant_id,
                    "quantity":   1,
                    "properties": {}
                }
            ]
        }

        var childProductCount = 0;

        flowWindow._flowState.step_data.forEach( stepData =>
        {
            stepData.products.forEach( product =>
            {
                for ( var i = 0; i < product.quantity; i++ )
                {
                    formData.items[ 0 ].properties[ `_bundle_child_variant_${ childProductCount }` ] = product.variant_id;
                    childProductCount ++;
                }
            } );
        } );

        genericAddToCart( formData, function( )
        {
            if ( flowWindow._flowData.type && flowWindow._flowData.type === "pdp" )
            {
                toggleFlowModal( false );
                flowWindow._flowState.bundle = flowWindow._flowData.bundles[ 0 ].id;
                flowWindow._flowState.selected_step = flowWindow._flowData.bundles[ 0 ].steps[ 0 ].id;
                flowWindow._flowState.step_data = [ ];
                renderFlow( );
            }
        } );
    }
}

function handleContinueButton( )
{
    const currentBundleIndex = GetIndexOfBundle( flowWindow._flowState.bundle );
    const currentStepIndex   = GetIndexOfStep( flowWindow._flowState.selected_step, flowWindow._flowState.bundle );
    const currentStep        = GetCurrentStep( );
    const currentQuantity    = getStepAddedQuantity( );

    if ( currentQuantity >= currentStep.min_quantity && currentQuantity <= currentStep.max_quantity )
    {
        const selectedProducts = ParseSelectedProducts( );
        WriteFlowState( flowWindow._flowState.bundle, flowWindow._flowState.selected_step, selectedProducts )
    }

    if ( currentStepIndex === flowWindow._flowData.bundles[ currentBundleIndex ].steps.length - 1 )
    {
        handleAddToCart( );
    }
}

/**
 * @param { boolean } open 
 */
function toggleSummary( open )
{
    const summary        = document.querySelector( ".js-bc-summary" );
    const summaryOverlay = document.querySelector( ".js-bc-summary-overlay" );
    const summaryBtn     = document.querySelector( ".js-bc-controls-summary" );

    function hideSummary( )
    {
        summary.classList.remove( "bundle-creator__summary--active" );
        summaryOverlay.classList.remove( "bundle-creator__summary-overlay--active" );
        if ( summaryBtn )
        {
            summaryBtn.setAttribute( "aria-expanded", "false" );
        }
    }

    function showSummary( )
    {
        summary.classList.add( "bundle-creator__summary--active" );
        summaryOverlay.classList.add( "bundle-creator__summary-overlay--active" );
        if ( summaryBtn )
        {
            summaryBtn.setAttribute( "aria-expanded", "true" );
        }
    }

    if ( open === true )
    {
        showSummary( );
    }
    else if ( open === false )
    {
        hideSummary( );
    }
    else
    {
        if ( summary.classList.contains( "bundle-creator__summary--active" ) )
        {
            hideSummary( );
        }
        else
        {
            showSummary( );
        }
    }
}

function handleSummaryButton( )
{
    const currentBundleIndex = GetIndexOfBundle( flowWindow._flowState.bundle );
    const currentStepIndex   = GetIndexOfStep( flowWindow._flowState.selected_step, flowWindow._flowState.bundle );

    // const selectedProducts = ParseSelectedProducts( );
    // WriteFlowState( flowWindow._flowState.bundle, flowWindow._flowState.selected_step, selectedProducts )

    if ( currentStepIndex === flowWindow._flowData.bundles[ currentBundleIndex ].steps.length - 1 )
    {
        renderSummary( )
    }

    toggleSummary( );
}

function handleBackButton( )
{
    if ( flowWindow._flowState.bundle && flowWindow._flowState.selected_step )
    {
        const currentBundleIndex = GetIndexOfBundle( flowWindow._flowState.bundle );
        const currentStepIndex   = GetIndexOfStep( flowWindow._flowState.selected_step, flowWindow._flowState.bundle );

        if ( currentStepIndex > 0 )
        {
            ResetFlowStateToStep( currentBundleIndex, currentStepIndex - 1 );
        }
        else if ( currentStepIndex === 0 )
        {
            flowWindow._flowState.bundle = null;
            flowWindow._flowState.step_data = null;
            flowWindow._flowState = [ ];
        }
        renderFlow( );
    }
}

function initSummaryModelEventListeners( )
{
    const summary          = document.querySelector( ".js-bc-summary" );
    const summaryCloseBtns = document.querySelectorAll( ".js-bc-summary-close" );

    summaryCloseBtns.forEach( summaryCloseBtn =>
    {
        summaryCloseBtn.addEventListener( "click", ( ) => { toggleSummary( false ); } );
    } )
}

function parseFlowData( flowData )
{
    const section = document.body.querySelector( ".bundle-creator" );
    if ( flowData )
    {
        try
        {
            const flowDataJSON = JSON.parse( flowData.innerHTML );
            flowWindow._flowData   = flowDataJSON;
        }
        catch ( error )
        {
            console.warn( "Error parsing bundle flow data - " + error );
            if ( section )
            {
                section.innerHTML = `<h2>There are currently no bundles.</h2>`;
            }
        }
    }
}

/**
 * @param { boolean } open 
 */
function toggleFlowModal( open )
{
    const modal        = document.querySelector( ".js-bcb-modal" );
    const modalOverlay = document.querySelector( ".js-bcb-modal-overlay" );

    function hideFlow( )
    {
        modal.classList.remove( "bundle-creator-block__modal--active" );
        modalOverlay.classList.remove( "bundle-creator-block__overlay--active" );
        document.body.classList.remove( "modal-show" );
    }

    function showFlow( )
    {
        modal.classList.add( "bundle-creator-block__modal--active" );
        modalOverlay.classList.add( "bundle-creator-block__overlay--active" );
        document.body.classList.add( "modal-show" );
    }

    if ( open === true )
    {
        showFlow( );
    }
    else if ( open === false )
    {
        hideFlow( );
    }
    else
    {
        if ( modal.classList.contains( "bundle-creator-block__modal--active" ) )
        {
            hideFlow( );
        }
        else
        {
            showFlow( );
        }
    }

}

function initBundleCreator( )
{
    const flowData       = document.querySelector( "#bundle-creation-flow-data" );
    const PDPFlowData    = document.querySelector( "#bundle-creation-flow-data-pdp" );
    const section        = document.body.querySelector( ".bundle-creator" );
    const backBtn        = section.querySelector( ".js-bc-controls-back" );
    const continueBtns   = section.querySelectorAll( ".js-bc-controls-continue" );
    const summaryBtn     = section.querySelector( ".js-bc-controls-summary" )
    const clearFilterBtn = section.querySelector( ".js-bc-controls-filters-clear" );
    const closeModalBtns = document.querySelectorAll( ".js-bcb-close" );

    if ( flowData )
    {
        parseFlowData( flowData );

        if ( localStorage.getItem( LOCAL_STORAGE_KEY_BASE + window.location.pathname.slice( 1 ) ) )
        {
            try
            {
                flowWindow._flowState = JSON.parse( localStorage.getItem( LOCAL_STORAGE_KEY_BASE + window.location.pathname.slice( 1 ) ) );
            }
            catch ( error )
            {
                console.warn( "Error parsing existing flow data - " + error );
            }
        }
    }
    else if ( PDPFlowData )
    {
        parseFlowData( PDPFlowData );
        
        const openModalBtn = document.querySelector( ".bundle-creator-block__open" );

        if ( openModalBtn )
        {
            openModalBtn.addEventListener( "click", toggleFlowModal );
        }

        flowWindow._flowState.selected_step = flowWindow._flowData.bundles[ 0 ].steps[ 0 ].id;
        flowWindow._flowState.bundle = flowWindow._flowData.bundles[ 0 ].id;
    }

    if ( flowWindow._flowData )
    {
        continueBtns.forEach( continueBtn =>
        {
            continueBtn.addEventListener( "click", handleContinueButton );
        } );
        summaryBtn.addEventListener( "click", handleSummaryButton )
        backBtn.addEventListener( "click", handleBackButton );
        clearFilterBtn.addEventListener( "click", clearFilters );
        closeModalBtns.forEach( closeModalBtn =>
        {
            closeModalBtn.addEventListener( "click", toggleFlowModal );
        } );

        renderFlow( );
        initSummaryModelEventListeners( );
    }
}

document.addEventListener( "DOMContentLoaded", initBundleCreator );